import type { PointApiResponse } from '../services/pointActivityService'

const DEBUG_STORAGE_KEY = 'adam_points_debug'
const TRACE_LIMIT = 300

type PointDebugLevel = 'info' | 'warn' | 'error'

export interface PointDebugRecord {
  at: string
  level: PointDebugLevel
  event: string
  details?: unknown
}

declare global {
  interface Window {
    __ADAM_POINTS_TRACE__?: PointDebugRecord[]
  }
}

function browserAvailable(): boolean {
  return typeof window !== 'undefined'
}

function queryRequestsDebug(): boolean {
  if (!browserAvailable()) return false

  try {
    return new URLSearchParams(window.location.search)
      .get('pointsDebug') === '1'
  } catch {
    return false
  }
}

export function isPointDebugEnabled(): boolean {
  if (!browserAvailable()) return false

  try {
    if (queryRequestsDebug()) {
      window.localStorage.setItem(DEBUG_STORAGE_KEY, '1')
      return true
    }

    return window.localStorage.getItem(DEBUG_STORAGE_KEY) === '1'
  } catch {
    return queryRequestsDebug()
  }
}

export function maskPointIdentifier(
  value?: string | null
): string | null {
  if (!value) return null

  if (value.length <= 8) {
    return `${value.slice(0, 2)}…${value.slice(-2)}`
  }

  return `${value.slice(0, 4)}…${value.slice(-4)}`
}

export function summarizePointResponse(
  response?: PointApiResponse | null
) {
  if (!response) return null

  return {
    wallet: {
      pointsTotal: response.wallet.pointsTotal,
      coinsTotal: response.wallet.coinsTotal,
      pointsResetAt: response.wallet.pointsResetAt,
      pointsCycleStartedAt: response.wallet.pointsCycleStartedAt,
      maxPointsBalance: response.wallet.maxPointsBalance,
      expirationDays: response.wallet.expirationDays,
    },
    dailyReview: {
      eligible: response.dailyReview.eligible,
      ineligibleReason: response.dailyReview.ineligibleReason,
      rewardPoints: response.dailyReview.rewardPoints,
      activeSeconds: response.dailyReview.activeSeconds,
      requiredActiveSeconds: response.dailyReview.requiredActiveSeconds,
      meaningfulActions: response.dailyReview.meaningfulActions,
      requiredMeaningfulActions: response.dailyReview.requiredMeaningfulActions,
      rewardedToday: response.dailyReview.rewardedToday,
      rewardedDaysThisCycle: response.dailyReview.rewardedDaysThisCycle,
      maxRewardsPerCycle: response.dailyReview.maxRewardsPerCycle,
    },
    mutation: {
      rewardClaimed: response.rewardClaimed ?? null,
      rewardGranted: response.rewardGranted ?? null,
      pointsAwarded: response.pointsAwarded ?? null,
      capReached: response.capReached ?? null,
    },
  }
}

function writePointDebug(
  level: PointDebugLevel,
  event: string,
  details?: unknown
) {
  if (!isPointDebugEnabled()) return

  const record: PointDebugRecord = {
    at: new Date().toISOString(),
    level,
    event,
    details,
  }

  if (browserAvailable()) {
    const trace = window.__ADAM_POINTS_TRACE__ ?? []
    trace.push(record)

    if (trace.length > TRACE_LIMIT) {
      trace.splice(0, trace.length - TRACE_LIMIT)
    }

    window.__ADAM_POINTS_TRACE__ = trace
  }

  const prefix = `[ADAM Points] ${event}`

  if (level === 'error') {
    console.error(prefix, details ?? '')
  } else if (level === 'warn') {
    console.warn(prefix, details ?? '')
  } else {
    console.info(prefix, details ?? '')
  }
}

export function pointDebug(event: string, details?: unknown) {
  writePointDebug('info', event, details)
}

export function pointDebugWarn(event: string, details?: unknown) {
  writePointDebug('warn', event, details)
}

export function pointDebugError(event: string, details?: unknown) {
  writePointDebug('error', event, details)
}
